<script setup lang="ts">
import {useUserAvatar} from "@/composables/avatar";
import {router} from "@inertiajs/vue3";
import {ref} from "vue";
import {DataTableHeader, ExtendedGroup, ExtendedProject} from "@/types";
import TextField from "@/components/forms/TextField.vue";
import RemoveUserDialog from "@/Pages/projects/groups/components/RemoveUserDialog.vue";

interface Props {
    project: ExtendedProject,
    groups: Array<ExtendedGroup>
}
const props = defineProps<Props>()
const emit = defineEmits(['click'])

const headers: Array<DataTableHeader> = [
    {title: 'Name', value: 'name', width: '20%'},
    {title: 'Members', value: 'members', width: '40%', align: 'end'},
    {title: 'Count', value: 'count', width: '20%', align: 'end'},
    {title: 'Actions', value: 'actions', width: '20%', align: 'end'},
]

const search = ref('')
</script>

<template>
    <v-card
        variant="flat"
        class="border-sm"
        rounded="lg"
    >
        <v-card-title class="bg-grey-lighten-4 text-subtitle-2 text-grey-darken-2">
            Groups
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="12">
                    <text-field
                        v-model="search"
                        placeholder="Find a group"
                        prepend-icon="mdi-magnify"
                        :hide-details="true"
                    />
                </v-col>
            </v-row>
        </v-card-text>
        <v-divider />
        <v-card-text class="pa-0">
            <v-data-table
                v-model:search="search"
                :headers="headers"
                :items="groups"
                :show-expand="true"
                no-data-text="No Results"
            >
                <template #expanded-row="{ item, columns }">
                    <td :colspan="columns.length">
                        <v-list
                            density="compact"
                        >
                            <v-list-item
                                v-for="user in item.users"
                                :key="user.email"
                            >
                                <template #prepend>
                                    <v-avatar
                                        size="36"
                                        class="border-sm"
                                        rounded="xl"
                                    >
                                        <v-img
                                            :src="useUserAvatar(user.name)"
                                            alt="avatar"
                                        />
                                    </v-avatar>
                                </template>
                                <v-list-item-title class="ml-5 text-body-2">
                                    {{ user.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="ml-5 text-body-2">
                                    {{ user.email }}
                                </v-list-item-subtitle>
                                <template #append>
                                    <remove-user-dialog
                                        :project="props.project"
                                        :user="user"
                                        :group="item"
                                        @confirm="router.reload({ only: ['groups'] })"
                                    />
                                </template>
                            </v-list-item>
                        </v-list>
                        <v-divider />
                    </td>
                </template>
                <template #[`item.name`]="{item}">
                    <a class="font-weight-bold text-body-2 text-primary">
                        {{ item.name }}
                    </a>
                </template>
                <template #[`item.members`]="{item}">
                    <v-avatar
                        v-for="user in item.users"
                        :key="user.email"
                        size="36"
                        class="border-sm mr-1"
                        rounded="xl"
                    >
                        <v-img
                            :src="useUserAvatar(user.name)"
                            alt="avatar"
                        />
                    </v-avatar>
                </template>
                <template #[`item.count`]="{item}">
                    <div class="text-grey-darken-1 font-weight-medium text-body-2">
                        {{ item.users.length }} members
                    </div>
                </template>
                <template #[`item.actions`]="{item}">
                    <v-btn
                        size="small"
                        variant="flat"
                        class="border text-none text-caption btn-outlined mr-5 bg-grey-lighten-4 rounded"
                        @click="$emit('click', item)"
                    >
                        <span class="font-weight font-weight-medium text-grey-darken-3">Add member</span>
                    </v-btn>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<style scoped>
:deep(thead) {
    display: none !important;
}

:deep(.v-data-table-footer) {
    display: none !important;
}

:deep(.v-list-item__prepend) {
  width: 30px;
}
</style>
